import { useState, useRef, useEffect, useCallback } from "react";

function stripMarkdown(text) {
  return (
    text
      // code blocks (``` ... ```)
      .replace(/```[\s\S]*?```/g, "")
      // inline code
      .replace(/`([^`]+)`/g, "$1")
      // images
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
      // links — keep the label text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // headings
      .replace(/^#{1,6}\s+/gm, "")
      // bold / italic
      .replace(/(\*{1,3}|_{1,3})(.+?)\1/g, "$2")
      // strikethrough
      .replace(/~~(.+?)~~/g, "$1")
      // blockquotes
      .replace(/^>\s+/gm, "")
      // horizontal rules
      .replace(/^[-*_]{3,}\s*$/gm, "")
      // unordered list markers
      .replace(/^\s*[-*+]\s+/gm, "")
      // ordered list markers
      .replace(/^\s*\d+\.\s+/gm, "")
      // HTML tags
      .replace(/<[^>]+>/g, "")
      // collapse multiple newlines
      .replace(/\n{2,}/g, "\n")
      .trim()
  );
}

export default function useSpeechSynthesis() {
  const [activeMessageId, setActiveMessageId] = useState(null);
  const utteranceRef = useRef(null);

  const isSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    utteranceRef.current = null;
    setActiveMessageId(null);
  }, [isSupported]);

  const speak = useCallback(
    (text, messageId) => {
      if (!isSupported) return;

      if (activeMessageId === messageId) {
        stop();
        return;
      }

      window.speechSynthesis.cancel();

      const cleaned = stripMarkdown(text);
      if (!cleaned) return;

      const utterance = new SpeechSynthesisUtterance(cleaned);
      utterance.lang = "en-US";
      utterance.rate = 1;
      utterance.pitch = 1;

      utterance.onend = () => {
        utteranceRef.current = null;
        setActiveMessageId(null);
      };

      utterance.onerror = (event) => {
        if (event.error === "canceled") return;
        utteranceRef.current = null;
        setActiveMessageId(null);
      };

      utteranceRef.current = utterance;
      setActiveMessageId(messageId);
      window.speechSynthesis.speak(utterance);
    },
    [isSupported, activeMessageId, stop]
  );

  return {
    isSpeaking: activeMessageId !== null,
    activeMessageId,
    speak,
    stop,
    isSupported,
  };
}
